import { CircularProgress } from '@mui/material'
import Grid from '@material-ui/core/Grid'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TextField from '@mui/material/TextField'
import React, { useState } from 'react'
import AlertDialog from '../../../../../../../components/AlertDialog/index.es6'
import ModalInc from '../../../../../../../shared-ui/ModalInc'
import ButtonInc from '../../../../../../../shared-ui/ButtonInc'
import { reloadPage } from '../../../../../../../lib/utils.es6'

// --- shared cell styles ---
const cellStyle = {
  fontFamily: 'Open Sans',
  fontSize: '12px',
  padding: '8px 12px'
}

const headerCellStyle = {
  fontFamily: 'Open Sans',
  fontSize: '12px',
  fontWeight: 600,
  padding: '8px 12px',
  backgroundColor: '#f5f5f5'
}

const selectedRowStyle = {
  backgroundColor: '#e3f2fd',
  cursor: 'pointer'
}

const rowStyle = { cursor: 'pointer' }

// --- helpers ---
const buildEmptyState = () => ({
  step: 1,
  transferMethod: null,
  msisdn: '',
  name: '',
  nationalId: '',
  externalIdentifier: '',
  selectedCustomer: null,
  selectedDependant: null
})

const getDependants = policy => {
  const entries =
    policy && policy.quotation && policy.quotation.policy_schedule_entries
      ? policy.quotation.policy_schedule_entries
      : []

  const fromEntries = entries
    .filter(e => e.dependant && e.dependant.relationship !== 'Principal')
    .map(e => e.dependant)

  const beneficiaries =
    policy && policy.beneficiaries ? policy.beneficiaries : []

  return fromEntries.concat(beneficiaries)
}

// --- component ---
const TransferPolicy = props => {
  const [step, setStep] = useState(1)
  const [transferMethod, setTransferMethod] = useState(null)
  const [msisdn, setMsisdn] = useState('')
  const [name, setName] = useState('')
  const [nationalId, setNationalId] = useState('')
  const [externalIdentifier, setExternalIdentifier] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedDependant, setSelectedDependant] = useState(null)

  if (!props.openTransferPolicyDialog) {
    return null
  }

  const resetLocalState = () => {
    const s = buildEmptyState()
    setStep(s.step)
    setTransferMethod(s.transferMethod)
    setMsisdn(s.msisdn)
    setName(s.name)
    setNationalId(s.nationalId)
    setExternalIdentifier(s.externalIdentifier)
    setSelectedCustomer(s.selectedCustomer)
    setSelectedDependant(s.selectedDependant)
  }

  const handleClose = () => {
    resetLocalState()
    props.resetFindCustomerForTransfer()
    props.toggleTransferPolicyDialog()
  }

  const handleSearch = () => {
    setSelectedCustomer(null)
    props.findCustomerForTransfer({
      msisdn,
      name,
      national_id: nationalId,
      external_identifier: externalIdentifier,
      page: 1,
      size: 10
    })
  }

  const handleConfirm = () => {
    const body = { policy_guid: props.policy.guid }
    if (transferMethod === 'search') {
      body.customer_guid = selectedCustomer.guid
    } else {
      body.beneficiary_guid = selectedDependant.guid
    }
    props.switchPolicyCustomer(body)
  }

  const handleSuccess = () => {
    props.resetSwitchPolicyCustomerSuccess()
    resetLocalState()
    props.resetFindCustomerForTransfer()
    props.toggleTransferPolicyDialog()
    reloadPage()
  }

  const handleError = () => {
    props.resetSwitchPolicyCustomerError()
  }

  const isSubmitting = props.switchPolicyCustomerLoader
  const showModal =
    !isSubmitting &&
    !props.switchPolicyCustomerSuccess &&
    !props.switchPolicyCustomerError

  const hasSearchInput = msisdn || name || nationalId || externalIdentifier

  const foundList =
    props.foundCustomersForTransfer && props.foundCustomersForTransfer.customers
      ? props.foundCustomersForTransfer.customers
      : Array.isArray(props.foundCustomersForTransfer)
      ? props.foundCustomersForTransfer
      : []

  const dependants = getDependants(props.policy)

  // --- step subtitles ---
  const stepSubtitles = {
    1: 'Choose how you want to select the new policy holder.',
    2:
      transferMethod === 'search'
        ? 'Search for the customer to transfer this policy to.'
        : 'Select a dependant or beneficiary from this policy.',
    3: 'Review the details below and confirm the transfer.'
  }

  // ---- Step 1: method selection ----
  const renderStep1 = () => (
    <div>
      <Grid container spacing={2} style={{ marginBottom: '24px' }}>
        <Grid item xs={12} sm={6} md={6}>
          <div
            onClick={() => {
              setTransferMethod('search')
              setSelectedDependant(null)
            }}
            style={{
              border:
                transferMethod === 'search'
                  ? '2px solid #032a6b'
                  : '2px solid #e0e0e0',
              borderRadius: '8px',
              padding: '20px 16px',
              cursor: 'pointer',
              backgroundColor:
                transferMethod === 'search' ? '#e8edf7' : '#fafafa',
              transition: 'all 0.15s'
            }}
          >
            <p
              style={{
                margin: '0 0 6px',
                fontWeight: 600,
                fontSize: '14px',
                color: '#032a6b'
              }}
            >
              Search for a customer
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
              Find any registered customer by phone, name, or ID.
            </p>
          </div>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <div
            onClick={() => {
              setTransferMethod('dependant')
              setSelectedCustomer(null)
              props.resetFindCustomerForTransfer()
            }}
            style={{
              border:
                transferMethod === 'dependant'
                  ? '2px solid #032a6b'
                  : '2px solid #e0e0e0',
              borderRadius: '8px',
              padding: '20px 16px',
              cursor: 'pointer',
              backgroundColor:
                transferMethod === 'dependant' ? '#e8edf7' : '#fafafa',
              transition: 'all 0.15s'
            }}
          >
            <p
              style={{
                margin: '0 0 6px',
                fontWeight: 600,
                fontSize: '14px',
                color: '#032a6b'
              }}
            >
              Select from policy dependants
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
              Transfer to an existing dependant or beneficiary on this policy.
            </p>
          </div>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <ButtonInc
            variant="contained"
            size="large"
            onClick={handleClose}
            style={{ width: '100%' }}
          >
            Cancel
          </ButtonInc>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <ButtonInc
            variant="contained"
            size="large"
            onClick={() => setStep(2)}
            disabled={!transferMethod}
            style={{ width: '100%' }}
          >
            Next
          </ButtonInc>
        </Grid>
      </Grid>
    </div>
  )

  // ---- Step 2A: customer search ----
  const renderSearchStep = () => (
    <div>
      <Grid container spacing={2} style={{ marginBottom: '16px' }}>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="Phone Number"
            value={msisdn}
            onChange={e => setMsisdn(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="Full Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="National ID"
            value={nationalId}
            onChange={e => setNationalId(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="External Identifier"
            value={externalIdentifier}
            onChange={e => setExternalIdentifier(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginBottom: '16px' }}>
        <Grid item xs={12} sm={6} md={6}>
          <ButtonInc
            variant="contained"
            size="large"
            onClick={handleSearch}
            disabled={!hasSearchInput || props.findCustomerForTransferLoader}
            style={{ width: '100%' }}
          >
            {props.findCustomerForTransferLoader ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Search'
            )}
          </ButtonInc>
        </Grid>
      </Grid>

      {props.findCustomerForTransferError && (
        <p
          style={{
            color: 'red',
            fontSize: '13px',
            marginBottom: '8px'
          }}
        >
          {typeof props.findCustomerForTransferError === 'string'
            ? props.findCustomerForTransferError
            : 'Could not search customers. Please try again.'}
        </p>
      )}

      {props.foundCustomersForTransfer && foundList.length === 0 && (
        <p style={{ fontSize: '13px', color: '#888' }}>
          No customers found. Try different search criteria.
        </p>
      )}

      {foundList.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={headerCellStyle}>Name</TableCell>
                <TableCell style={headerCellStyle}>Phone</TableCell>
                <TableCell style={headerCellStyle}>National ID</TableCell>
                <TableCell style={headerCellStyle}>External ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {foundList.map(customer => {
                const isSelected =
                  selectedCustomer && selectedCustomer.guid === customer.guid
                return (
                  <TableRow
                    key={customer.guid}
                    onClick={() => setSelectedCustomer(customer)}
                    style={isSelected ? selectedRowStyle : rowStyle}
                    hover
                  >
                    <TableCell style={cellStyle}>
                      {customer.first_name || ''} {customer.last_name || ''}
                    </TableCell>
                    <TableCell style={cellStyle}>
                      {customer.msisdn || 'N/A'}
                    </TableCell>
                    <TableCell style={cellStyle}>
                      {customer.national_id || 'N/A'}
                    </TableCell>
                    <TableCell style={cellStyle}>
                      {customer.external_identifier || 'N/A'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Grid container spacing={2} style={{ marginTop: '8px' }}>
        <Grid item xs={12} sm={6} md={6}>
          <ButtonInc
            variant="contained"
            size="large"
            onClick={() => setStep(1)}
            style={{ width: '100%' }}
          >
            Back
          </ButtonInc>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <ButtonInc
            variant="contained"
            size="large"
            onClick={() => setStep(3)}
            disabled={!selectedCustomer}
            style={{ width: '100%' }}
          >
            Next
          </ButtonInc>
        </Grid>
      </Grid>
    </div>
  )

  // ---- Step 2B: dependant / beneficiary selection ----
  const renderDependantStep = () => (
    <div>
      {dependants.length === 0 && (
        <p
          style={{
            fontSize: '13px',
            color: '#888',
            marginBottom: '16px'
          }}
        >
          No dependants or beneficiaries found on this policy.
        </p>
      )}

      {dependants.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={headerCellStyle}>Name</TableCell>
                <TableCell style={headerCellStyle}>Relationship</TableCell>
                <TableCell style={headerCellStyle}>Phone</TableCell>
                <TableCell style={headerCellStyle}>National ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dependants.map(dep => {
                const isSelected =
                  selectedDependant && selectedDependant.guid === dep.guid
                const fullName = `${dep.first_name || ''} ${dep.last_name ||
                  ''}`
                return (
                  <TableRow
                    key={dep.guid}
                    onClick={() => setSelectedDependant(dep)}
                    style={isSelected ? selectedRowStyle : rowStyle}
                    hover
                  >
                    <TableCell style={cellStyle}>
                      {fullName.trim() || 'N/A'}
                    </TableCell>
                    <TableCell style={cellStyle}>
                      {dep.relationship || 'N/A'}
                    </TableCell>
                    <TableCell style={cellStyle}>
                      {dep.msisdn || 'N/A'}
                    </TableCell>
                    <TableCell style={cellStyle}>
                      {dep.national_id || 'N/A'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Grid container spacing={2} style={{ marginTop: '8px' }}>
        <Grid item xs={12} sm={6} md={6}>
          <ButtonInc
            variant="contained"
            size="large"
            onClick={() => setStep(1)}
            style={{ width: '100%' }}
          >
            Back
          </ButtonInc>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <ButtonInc
            variant="contained"
            size="large"
            onClick={() => setStep(3)}
            disabled={!selectedDependant}
            style={{ width: '100%' }}
          >
            Next
          </ButtonInc>
        </Grid>
      </Grid>
    </div>
  )

  // ---- Step 3: confirmation ----
  const renderConfirmStep = () => {
    const isSearchPath = transferMethod === 'search'
    const recipient = isSearchPath ? selectedCustomer : selectedDependant
    const recipientName = recipient
      ? `${recipient.first_name || ''} ${recipient.last_name || ''}`.trim()
      : 'N/A'
    const recipientPhone = recipient ? recipient.msisdn || 'N/A' : 'N/A'
    const recipientId = isSearchPath
      ? null
      : recipient
      ? recipient.national_id || null
      : null
    const recipientRelationship =
      !isSearchPath && recipient ? recipient.relationship || null : null
    const selectionLabel = isSearchPath
      ? 'Customer Search'
      : 'Policy Dependant / Beneficiary'

    return (
      <div>
        <Grid
          container
          spacing={2}
          style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '4px'
          }}
        >
          <Grid item xs={12}>
            <p
              style={{
                fontWeight: 600,
                marginBottom: '12px',
                fontSize: '13px'
              }}
            >
              Policy
            </p>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <p
              style={{
                margin: '4px 0',
                fontSize: '12px',
                color: '#A9A9A9'
              }}
            >
              Policy Number
            </p>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
              {props.policy.policy_number || 'N/A'}
            </p>
          </Grid>

          <Grid item xs={12} style={{ marginTop: '12px' }}>
            <p
              style={{
                fontWeight: 600,
                marginBottom: '12px',
                fontSize: '13px'
              }}
            >
              Transfer Recipient
            </p>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <p
              style={{
                margin: '4px 0',
                fontSize: '12px',
                color: '#A9A9A9'
              }}
            >
              Name
            </p>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
              {recipientName || 'N/A'}
            </p>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <p
              style={{
                margin: '4px 0',
                fontSize: '12px',
                color: '#A9A9A9'
              }}
            >
              Phone Number
            </p>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
              {recipientPhone}
            </p>
          </Grid>
          {recipientId && (
            <Grid item xs={12} sm={6} md={6}>
              <p
                style={{
                  margin: '4px 0',
                  fontSize: '12px',
                  color: '#A9A9A9'
                }}
              >
                National ID
              </p>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
                {recipientId}
              </p>
            </Grid>
          )}
          {recipientRelationship && (
            <Grid item xs={12} sm={6} md={6}>
              <p
                style={{
                  margin: '4px 0',
                  fontSize: '12px',
                  color: '#A9A9A9'
                }}
              >
                Relationship
              </p>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
                {recipientRelationship}
              </p>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={6}>
            <p
              style={{
                margin: '4px 0',
                fontSize: '12px',
                color: '#A9A9A9'
              }}
            >
              Selected via
            </p>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
              {selectionLabel}
            </p>
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '8px' }}>
          <Grid item xs={12} sm={6} md={6}>
            <ButtonInc
              variant="contained"
              size="large"
              onClick={() => setStep(1)}
              disabled={isSubmitting}
              style={{ width: '100%' }}
            >
              Back
            </ButtonInc>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <ButtonInc
              variant="contained"
              size="large"
              onClick={handleConfirm}
              disabled={isSubmitting}
              style={{
                width: '100%',
                background: '#032a6b',
                color: '#ffffff'
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Confirm Transfer'
              )}
            </ButtonInc>
          </Grid>
        </Grid>
      </div>
    )
  }

  const renderStepContent = () => {
    if (step === 1) return renderStep1()
    if (step === 2 && transferMethod === 'dependant')
      return renderDependantStep()
    if (step === 2) return renderSearchStep()
    return renderConfirmStep()
  }

  return (
    <>
      <AlertDialog
        custom
        show={isSubmitting}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        confirmBtnText="ok"
        showCancel={false}
        showConfirm={false}
        cancelBtnText="cancel"
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Transferring policy...</h2>
      </AlertDialog>

      <ModalInc
        modalTitle="Transfer Policy"
        subTitle={stepSubtitles[step] || ''}
        open={showModal}
        close={handleClose}
        fullWidth
        maxWidth="md"
      >
        {renderStepContent()}
      </ModalInc>

      <AlertDialog
        success
        show={props.switchPolicyCustomerSuccess}
        size="sm"
        title="Policy transferred successfully"
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={handleSuccess}
        confirmBtnText="Ok"
        showCancel={false}
        showConfirm={false}
        cancelBtnText="ok"
        confirmBtnCssClass
      >
        <div>
          <ButtonInc
            onClick={handleSuccess}
            style={{ background: 'green', color: '#ffffff' }}
          >
            Ok
          </ButtonInc>
        </div>
      </AlertDialog>

      <AlertDialog
        show={!!props.switchPolicyCustomerError}
        danger
        title="Error transferring policy"
        onConfirm={handleError}
        confirmBtnText="Try again"
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        {props.switchPolicyCustomerError
          ? typeof props.switchPolicyCustomerError === 'string'
            ? props.switchPolicyCustomerError
            : 'Could not transfer policy. Please try again.'
          : 'Could not transfer policy. Please try again.'}
      </AlertDialog>
    </>
  )
}

export default TransferPolicy
